import { formatDistanceMeters, formatDurationSeconds, formatRideDate, type RouteSource } from '@motogo/shared';
import { ArrowLeft, Plus } from 'lucide-react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import RideSparkline from './RideSparkline';

import { useAuth } from '@/hooks/useAuth';
import { useSavedRoutes } from '@/hooks/useSavedRoutes';

const SOURCE_LABELS: Record<RouteSource, string> = {
  planner: 'Planned',
  gpx: 'GPX',
  ride: 'From ride',
  editor: 'Custom',
};

// The saved-route library: server routes (GET /me/routes) merged with the local mirror,
// each card a sparkline thumbnail linking to /routes/:id. Mirrors RideListPage — the
// sparkline is reused as-is (it only needs an encoded polyline).
const RouteLibraryPage: FC = () => {
  const navigate = useNavigate();
  const { exitGuestMode } = useAuth();
  const { state, refresh } = useSavedRoutes();

  return (
    <div className="mg-page">
      <header className="mg-page-header">
        {/* Explicit target (not navigate(-1)) so the button always lands on the map,
            even when the list was reached via a deep link with no history behind it. */}
        <button className="mg-icon-btn" onClick={() => navigate('/')} aria-label="Back to map" title="Back to map">
          <ArrowLeft size={26} />
        </button>
        <h1 className="mg-page-title" style={{ flex: 1 }}>Routes</h1>
        {/* M3 waypoint editor — draw a route on the map */}
        <button className="mg-icon-btn" onClick={() => navigate('/routes/new')} aria-label="Create route" title="Create route">
          <Plus size={26} />
        </button>
      </header>

      {state.status === 'loading' && (
        <div className="mg-page-body">
          <p className="mg-empty">Loading routes…</p>
        </div>
      )}

      {state.status === 'signedout' && (
        <div className="mg-page-body">
          <p className="mg-empty">Sign in to save routes</p>
          <p className="mg-empty-sub">Saved routes live with your account so you can share and reuse them on any device.</p>
          <button
            className="mg-btn mg-btn-primary"
            style={{ width: 'auto', padding: '10px 24px', marginTop: 12 }}
            onClick={exitGuestMode}
          >
            Sign in
          </button>
        </div>
      )}

      {state.status === 'error' && (
        <div className="mg-page-body">
          <p className="mg-empty">Couldn&rsquo;t load routes</p>
          <p className="mg-empty-sub">{state.error}</p>
          <button
            className="mg-btn mg-btn-outline"
            style={{ width: 'auto', padding: '10px 24px', marginTop: 12 }}
            onClick={refresh}
          >
            Retry
          </button>
        </div>
      )}

      {state.status === 'ready' && state.routes.length === 0 && (
        <div className="mg-page-body">
          <p className="mg-empty">No saved routes yet</p>
          <p className="mg-empty-sub">
            Plan a route or load a GPX in the planner and save it — or create one waypoint by waypoint with +.
          </p>
        </div>
      )}

      {state.status === 'ready' && state.routes.length > 0 && (
        <>
          {state.offline && <p className="mg-empty-sub" style={{ textAlign: 'center', marginTop: 8 }}>Offline — showing stored copies</p>}
          <ul className="mg-ride-list">
            {state.routes.map((route) => (
              <li key={route.id}>
                <button className="mg-ride-card" onClick={() => navigate(`/routes/${route.id}`)}>
                  <RideSparkline polyline={route.overviewPolyline} />
                  <div className="mg-ride-meta">
                    <span className="mg-ride-name">{route.name}</span>
                    <span className="mg-ride-date">
                      {route.owned === false && route.ownerName
                        ? `by ${route.ownerName}`
                        : SOURCE_LABELS[route.source]}
                      {' · '}
                      {route.isFrozen ? 'no longer updated' : formatRideDate(route.createdAt)}
                    </span>
                    <div className="mg-ride-stats">
                      <span>{formatDistanceMeters(route.distanceMeters)}</span>
                      <span>{route.durationSeconds !== null ? formatDurationSeconds(route.durationSeconds) : '—'}</span>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default RouteLibraryPage;
